import React, { useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

//function of excel download
const handleDownload = () => {
    const tableSelect = document.getElementById('salesDataReport'); // Select table by ID
    if (!tableSelect) return;

    const dataType = 'application/vnd.ms-excel';
    const tableHTML = tableSelect.outerHTML.replace(/ /g, '%20');

    const downloadLink = document.createElement('a');
    document.body.appendChild(downloadLink);

    downloadLink.href = 'data:' + dataType + ', ' + tableHTML;
    downloadLink.download = 'sales_data_summary.xls';
    downloadLink.click();
    document.body.removeChild(downloadLink);
};

const fetchSalesSummary = async ({ month, year }) => {
    const response = await fetch(`${process.env.REACT_APP_URL_SALE}/api/salesdata`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ month, year }),
    });
    if (!response.ok) {
        throw new Error('Failed to fetch data');
    }
    return response.json();
};

const Sales_Data_Summary = ({ month, year, heading }) => {
    const [reportData, setReportData] = useState([]);
    const [commodities, setCommodities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const detailsRef = useRef(null);
    const [totals, setTotals] = useState({
        totalCards: 0,
        totalUnits: 0,
        soldCards: 0,
        soldUnits: 0,
        commodityQty: {},
    });

    const calculateTotals = (rows, comms) => {
    const agg = {
        totalCards: 0,
        totalUnits: 0,
        soldCards: 0,
        soldUnits: 0,
        commodityQty: {},
    };

    comms.forEach(c => agg.commodityQty[c] = 0);

    rows.forEach(row => {
        agg.totalCards += row.totalCards;
        agg.totalUnits += row.totalUnits;
        agg.soldCards += row.soldCards;
        agg.soldUnits += row.soldUnits;

        comms.forEach(c => {
            agg.commodityQty[c] += parseFloat(row.commodityQty?.[c] || 0);
        });
    });

    setTotals(agg);
};


    useEffect(() => {
    const getData = async () => {
        try {
            const apiData = await fetchSalesSummary({ month, year });
            const rows = Array.isArray(apiData) ? apiData : [];

            if (rows.length === 0) {
                setReportData([]);
                return;
            }

            // 🔹 Extract ALL dynamic commodities
            const commSet = new Set();
            rows.forEach(r => {
                if (r.qty) {
                    Object.keys(r.qty).forEach(c => commSet.add(c));
                }
            });
            const comms = Array.from(commSet);

            // 🔹 Normalize rows for table
            const finalRows = rows.map(r => ({
                scheme: r.scheme_name,
                totalCards: r.total_cards || 0,
                totalUnits: r.total_units || 0,
                soldCards: r.avail_cards || 0,
                soldUnits: r.total_units || 0, // adjust if backend gives sold units separately
                commodityQty: r.qty || {}
            }));

            setCommodities(comms);
            setReportData(finalRows);
            calculateTotals(finalRows, comms);

        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    };
    getData();
}, [month, year]);


    useEffect(() => {
        if (!loading && !error && reportData.length > 0 && detailsRef.current) {
            detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [loading, error, reportData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ display: 'flex' }}>
                    <CircularProgress />
                </Box>
            </div>
        );
    }
    if (error) {
        return <div className="mainhead" align="center">Internal Error</div>;
    }
    if (reportData.length === 0) {
        return <div className="mainhead" align="center">No data found</div>;
    }

    const formatNumber = (value) => {
        const num = parseFloat(value);
        if (Number.isNaN(num)) {
            return '0.00';
        }
        return num.toLocaleString('en-IN', {
            maximumFractionDigits: 4,
        });
    };

    return (
        <>
            <table align="center" width="90%" border={1} id="salesDataReport">
                <thead>
                    <tr className="tableheader">
                        <th colSpan={5 + commodities.length}>{heading || 'Sale Summary'}</th>
                    </tr>
                    <tr className="tableheader">
                        <th rowSpan={2}>Scheme</th>
                        <th colSpan={2}>Total</th>
                        <th colSpan={1}>Sold</th>
                        {commodities.length > 0 && <th colSpan={commodities.length}>Sold Qty</th>}
                    </tr>
                    <tr className="tableheader">
                        <th>Cards</th>
                        <th>Units</th>
                        <th>Cards</th>
                        {commodities.map(c => (
    <th key={c}>{c}</th>
))}

                    </tr>
                </thead>
                <tbody>
                    {reportData.map((row) => (
                        <tr key={row.scheme}>
                            <td style={{ textAlign: 'left' }}>{row.scheme}</td>
                            <td style={{ textAlign: 'right' }}>{formatNumber(row.totalCards)}</td>
                            <td style={{ textAlign: 'right' }}>{formatNumber(row.totalUnits)}</td>
                            <td style={{ textAlign: 'right' }}>{formatNumber(row.soldCards)}</td>
                            {commodities.map(c => (
    <td key={`${row.scheme}-${c}`} style={{ textAlign: 'right' }}>
        {formatNumber(row.commodityQty?.[c] || 0)}
    </td>
))}

                        </tr>
                    ))}
                    <tr className="tableheader">
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatNumber(totals.totalCards)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatNumber(totals.totalUnits)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatNumber(totals.soldCards)}</td>
                        {commodities.length > 0 && commodities.map((c) => (
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }} key={`total-${c}`}>
                                {formatNumber(totals.commodityQty[c] || 0)}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>

            <div align="center">
                <button type="button" className="button button2" onClick={handleDownload}>Data to Excel</button>
            </div>

            <br />
            <div id="salesDataDetails" ref={detailsRef}></div>
        </>
    );
};

export default Sales_Data_Summary;


